import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import {
  fetchClientConversation,
  sendClientReply,
  saveCustomerName,
  fetchTemplates,
  fetchConversationState,
  resumeConversationAutomation,
} from '../../services/api';
import { useChatContext } from '../../context/ChatContext';
import { useNotification } from '../../context/NotificationContext';
import { Phone, Search, Send, Smile, Paperclip, Mic, X, ArrowUp, ArrowDown, Edit2, Check, Loader2, ChevronLeft } from 'lucide-react';
import TickIcon from './TickIcon';
import Avatar from './Avatar';

/** Renders a WhatsApp-style template message card with header, body, footer, and buttons */
const TemplateMsgCard = ({ msg, searchQuery, highlightText, templates }) => {
  const comps = useMemo(() => {
    if (!templates || !msg.template_name) return null;
    const t = templates.find(t => t.name?.toLowerCase() === msg.template_name?.toLowerCase());
    if (!t) return null;
    return t.components || [];
  }, [templates, msg.template_name]);

  const params = msg.params || [];

  // If no template found in cache, fall back to plain body text
  if (!comps) {
    if (msg.template_name) {
      console.log(`[TemplateMsgCard] Template structure NOT found for "${msg.template_name}". Available templates:`, templates?.map(t => t.name));
    }
    return <span>{highlightText(msg.text || '', searchQuery)}</span>;
  }

  // Count header params to know where body params start
  const headerComp = comps.find(c => (c.type || '').toUpperCase() === 'HEADER');
  const bodyComp = comps.find(c => (c.type || '').toUpperCase() === 'BODY');
  const footerComp = comps.find(c => (c.type || '').toUpperCase() === 'FOOTER');
  const buttonsComp = comps.find(c => (c.type || '').toUpperCase() === 'BUTTONS');

  const headerVarCount = (headerComp?.text || '').match(/\{\{\d+\}\}/g)?.length || 0;
  const headerParams = params.slice(0, headerVarCount);
  const bodyParams = params.slice(headerVarCount);

  const fillVars = (text, p) => {
    let result = text || '';
    p.forEach((val, i) => { result = result.replace(new RegExp(`\\{\\{${i + 1}\\}\\}`, 'g'), val); });
    return result;
  };

  const headerText = headerComp?.format === 'TEXT' ? fillVars(headerComp.text, headerParams) : null;
  const headerMedia = headerComp && headerComp.format !== 'TEXT' ? headerComp.format?.toLowerCase() : null;
  const bodyText = bodyComp ? fillVars(bodyComp.text, bodyParams) : (msg.text || '');
  const footerText = footerComp?.text || null;
  const buttons = buttonsComp?.buttons || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 180, maxWidth: 340 }}>
      {headerText && (
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{highlightText(headerText, searchQuery)}</div>
      )}
      {headerMedia && !headerText && (
        <div style={{ fontSize: 12, color: '#999', fontStyle: 'italic', marginBottom: 2 }}>📎 [{headerMedia} attachment]</div>
      )}
      <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{highlightText(bodyText, searchQuery)}</div>
      {footerText && (
        <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{footerText}</div>
      )}
      {buttons.length > 0 && (
        <div style={{ marginTop: 6, borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {buttons.map((btn, i) => (
            <div key={i} style={{ color: '#00a884', fontSize: 13, fontWeight: 500, textAlign: 'center', padding: '4px 0', cursor: 'default' }}>
              {btn.type === 'URL' ? '🔗 ' : btn.type === 'PHONE_NUMBER' ? '📞 ' : '↩ '}{btn.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ChatWindow = forwardRef(({ customerId, customerName: initialName, onNameUpdate, onBack }, ref) => {
  const { getConversation, setConversation, prefetchConversation, reloadCustomers, wsIncomingMsg, wsStatusUpdate } = useChatContext();
  const { showSuccess, showError } = useNotification();
  const [messages, setMessages] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [resumingAutomation, setResumingAutomation] = useState(false);
  const [customerName, setCustomerName] = useState(initialName);
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(initialName || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [templates, setTemplates] = useState([]);
  const [conversationState, setConversationState] = useState({
    automation_paused: false,
    automation_pause_reason: null,
    status: 'active',
  });
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [currentMatch, setCurrentMatch] = useState(0);
  const matchRefs = useRef([]);
  const loadingRef = useRef(false); // Track if already loading to prevent duplicate calls
  const customerIdRef = useRef(customerId); // Track customerId for stale-closure guards
  useEffect(() => { customerIdRef.current = customerId; }, [customerId]);

  // Fetch templates once so we can render template cards in the chat
  useEffect(() => {
    fetchTemplates().then(({ data }) => setTemplates(data || [])).catch(() => {});
  }, []);

  const loadConversationState = useCallback(async () => {
    if (!customerId) {
      setConversationState({
        automation_paused: false,
        automation_pause_reason: null,
        status: 'active',
      });
      return;
    }
    try {
      const { data } = await fetchConversationState(customerId);
      setConversationState({
        automation_paused: !!data?.automation_paused,
        automation_pause_reason: data?.automation_pause_reason || null,
        status: data?.status || 'active',
      });
    } catch (error) {
      console.error('Failed to load conversation state:', error);
    }
  }, [customerId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const handleSearchClick = () => {
    setIsSearchVisible(v => !v);
    setSearchQuery('');
    setCurrentMatch(0);
  };


  const scrollToBottom = useCallback((behavior = 'smooth') => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  }, []);

  const pendingReloadRef = useRef(false);

  const loadConversation = useCallback(async (options = {}) => {
    const force = options.force === true;
    if (!customerId) {
      setMessages([]);
      return;
    }

    // Prevent duplicate simultaneous loads
    if (loadingRef.current) {
      if (force) {
        pendingReloadRef.current = true;
        console.log('[ChatWindow] Load in flight; queueing force reload');
      } else {
        console.log('[ChatWindow] Already loading, skipping duplicate call');
      }
      return;
    }

    loadingRef.current = true;
    setLoading(true);

    try {
      // First try cache for instant render
      const cached = getConversation(customerId);
      if (cached) {
        setAllMessages(cached);
        setMessages(cached);
        setLoading(false);
        loadingRef.current = false;
        // Background refresh - but DON'T replace messages if user is actively chatting
        // Just update the cache silently
        fetchClientConversation(customerId).then(({ data }) => {
          setConversation(customerId, data);
          
          const mergeOptimistic = (prev) => {
            const dataWaIds = new Set(data.filter(m => m.wa_message_id).map(m => m.wa_message_id));
            const consumedUiIds = new Set();
            const duplicateServerWaIds = data
              .filter(m => m.wa_message_id)
              .map(m => m.wa_message_id)
              .filter((waId, index, arr) => arr.indexOf(waId) !== index);
            
            const statusRank = { 'sending': 0, 'sent': 1, 'delivered': 2, 'read': 3, 'failed': 4 };
            
            const smartData = data.map(serverMsg => {
              if (serverMsg.direction === 'in') return serverMsg;
              
              let uiMsg = null;
              
              if (serverMsg.wa_message_id) {
                uiMsg = prev.find(p => p.wa_message_id === serverMsg.wa_message_id);
              } else {
                // Server doesn't have wa_message_id yet. Match by text.
                // We must find an optimistic message that hasn't been consumed yet.
                uiMsg = prev.find(p => p.direction === 'out' && p.text === serverMsg.text && !consumedUiIds.has(p.id));
              }
              
              if (!uiMsg) return serverMsg;
              
              consumedUiIds.add(uiMsg.id); // mark as consumed
              
              const uiRank = statusRank[uiMsg.status] || 0;
              const serverRank = statusRank[serverMsg.status] || 0;
              
              return {
                ...serverMsg,
                status: uiRank > serverRank ? uiMsg.status : serverMsg.status,
                wa_message_id: uiMsg.wa_message_id || serverMsg.wa_message_id,
                template_name: serverMsg.template_name || uiMsg.template_name,
                params: serverMsg.params || uiMsg.params,
              };
            });
            
            // Keep any purely optimistic messages the server doesn't know about yet
            // (i.e. those that were NOT matched and consumed during smartData mapping)
            const optimisticMessages = prev.filter(m => 
              m.direction === 'out' && 
              !consumedUiIds.has(m.id) && 
              (!m.wa_message_id || !dataWaIds.has(m.wa_message_id))
            );
            
            if (optimisticMessages.length > 0 || duplicateServerWaIds.length > 0) {
              // #region agent log
              fetch('http://127.0.0.1:7813/ingest/09c3a12d-3025-469f-96cf-0d890faa877f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'477b4c'},body:JSON.stringify({sessionId:'477b4c',runId:'pre-fix',hypothesisId:'H3',location:'frontend/src/components/chat/ChatWindow.jsx:199',message:'conversation_merge_analysis',data:{customerId,serverCount:data.length,cachedCount:Array.isArray(prev)?prev.length:null,optimisticCount:optimisticMessages.length,duplicateServerWaIdCount:duplicateServerWaIds.length},timestamp:Date.now()})}).catch(()=>{});
              // #endregion
            }

            if (optimisticMessages.length > 0) {
              console.log('[ChatWindow] 🔄 Merging server ticks with optimistic draft messages');
              return [...smartData, ...optimisticMessages].sort((a,b) => new Date(a.ts) - new Date(b.ts));
            }
            return smartData;
          };

          setMessages(prev => mergeOptimistic(prev));
          setAllMessages(prev => mergeOptimistic(prev));
        }).catch(() => { });
        return;
      }
      const { data } = await fetchClientConversation(customerId);
      setAllMessages(data);
      setMessages(data);
      setConversation(customerId, data);
    } catch (error) {
      console.error("Failed to fetch conversation:", error);
    } finally {
      setLoading(false);
      loadingRef.current = false;
      if (pendingReloadRef.current) {
        pendingReloadRef.current = false;
        console.log('[ChatWindow] Running queued force reload');
        setTimeout(() => loadConversation({ force: true }), 0);
      }
    }
  }, [customerId, getConversation, setConversation]); // Include all dependencies properly

  useImperativeHandle(ref, () => ({
    requestLoadConversation() {
      loadConversation();
    }
  }));

  useEffect(() => {
    setCustomerName(initialName);
  }, [initialName]);

  // Sync tempName when initialName changes
  useEffect(() => {
    setTempName(initialName || '');
  }, [initialName]);

  useEffect(() => {
    loadConversation();
  }, [customerId, loadConversation]); // Include loadConversation properly

  useEffect(() => {
    loadConversationState();
  }, [customerId, loadConversationState]);

  // ALWAYS scroll to bottom whenever messages change (like WhatsApp)
  // This ensures chat is always at the bottom, no jiggle or oscillation
  useEffect(() => {
    // Skip scrolling when user is searching
    if (isSearchVisible) return;

    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      scrollToBottom('auto'); // Use 'auto' for instant scroll (no animation = no jiggle)
    });
  }, [messages, scrollToBottom, isSearchVisible]);

  // Listen for targeted incoming messages — read from cache first (instant), then background refresh
  useEffect(() => {
    if (wsIncomingMsg && wsIncomingMsg.customerId === customerId) {
      console.log('[ChatWindow] 📩 Incoming message targeted for current customer:', customerId);
      // Read directly from cache (already updated by ChatContext)
      const cached = getConversation(customerId);
      if (cached) {
        setMessages(cached);
        setAllMessages(cached);
      }
      // Also do a background refresh to ensure consistency, but don't block the UI
      const timeoutId = setTimeout(() => {
        loadConversation({ force: true });
      }, 2000); // Delayed background sync
      return () => clearTimeout(timeoutId);
    }
  }, [wsIncomingMsg, customerId, getConversation, loadConversation]);

  // Listen for WebSocket status updates and update message status smoothly (no reload)
  useEffect(() => {
    if (!wsStatusUpdate || !customerId) return;

    // Only update if this status update is for the current customer
    if (wsStatusUpdate.customer_id === customerId) {
      console.log('[ChatWindow] 📊 Status update for current customer:', wsStatusUpdate);

      const findAndUpdateMsg = (prevArray) => {
        if (!Array.isArray(prevArray)) return prevArray;
        
        let targetIndex = prevArray.findIndex(m => m.wa_message_id && m.wa_message_id === wsStatusUpdate.wa_message_id);
        
        // Fallback: match LAST outgoing message without wa_message_id
        if (targetIndex === -1) {
          for (let i = prevArray.length - 1; i >= 0; i--) {
            const m = prevArray[i];
            if (!m.wa_message_id && m.direction === 'out' && (m.status === 'sending' || m.status === 'sent')) {
              targetIndex = i;
              break;
            }
          }
        }
        
        if (targetIndex === -1) return prevArray;

        const updated = [...prevArray];
        const msg = updated[targetIndex];
        
        updated[targetIndex] = {
          ...msg,
          status: wsStatusUpdate.status,
          wa_message_id: wsStatusUpdate.wa_message_id || msg.wa_message_id,
        };
        
        return updated;
      };

      setMessages(prev => findAndUpdateMsg(prev));
      setAllMessages(prev => findAndUpdateMsg(prev));
      setConversation(customerId, prev => findAndUpdateMsg(prev));
    }
  }, [wsStatusUpdate, customerId]);

  useEffect(() => {
    setSearchQuery('');
  }, [customerId]);

  useEffect(() => {
    if (searchQuery) {
      const filtered = allMessages.filter(m => m.text.toLowerCase().includes(searchQuery.toLowerCase()));
      setMessages(filtered);
      setCurrentMatch(0);
    } else {
      setMessages(allMessages);
    }
  }, [searchQuery, allMessages]);

  // Scroll to current match when searching
  useEffect(() => {
    if (!isSearchVisible || !searchQuery) return;
    const el = matchRefs.current[currentMatch];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentMatch, messages, isSearchVisible, searchQuery]);

  const nextMatch = () => {
    if (!searchQuery || messages.length === 0) return;
    setCurrentMatch(i => (i + 1) % messages.length);
  };
  const prevMatch = () => {
    if (!searchQuery || messages.length === 0) return;
    setCurrentMatch(i => (i - 1 + messages.length) % messages.length);
  };

  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const highlightText = (text, query) => {
    if (!query) return text;
    const safe = escapeRegExp(query);
    const parts = (text || '').split(new RegExp(`(${safe})`, 'ig'));
    return parts.map((part, idx) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark className="search-mark" key={idx}>{part}</mark>
      ) : (
        <React.Fragment key={idx}>{part}</React.Fragment>
      )
    );
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (input.trim() === '' || !customerId) return;

    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      text: input,
      direction: 'out',
      ts: new Date().toISOString(),
      status: 'sending'
    };

    setMessages(prevMessages => [...prevMessages, optimisticMessage]);
    setAllMessages(prevMessages => [...prevMessages, optimisticMessage]);
    setConversation(customerId, (getConversation(customerId) || []).concat(optimisticMessage));
    setInput('');

    try {
      await sendClientReply(customerId, input);
      await loadConversationState();

      // The backend now automatically broadcasts a `message_status_update` via WebSocket 
      // the exact millisecond it successfully transmits to Meta. This broadcast contains 
      // the real `wa_message_id` and the `status: 'sent'` tick. 
      // By relying purely on the WebSocket, we eliminate all race conditions!

      // Reload customers to update the latest message preview text in the sidebar
      if (reloadCustomers) reloadCustomers();

    } catch (error) {
      console.error("Failed to send message:", error);
      setMessages(prevMessages => prevMessages.filter(msg => msg.id !== optimisticMessage.id));
      setAllMessages(prevMessages => prevMessages.filter(msg => msg.id !== optimisticMessage.id));
    }
  };

  const handleResumeAutomation = async () => {
    if (!customerId) return;
    setResumingAutomation(true);
    try {
      await resumeConversationAutomation(customerId);
      setConversationState(prev => ({
        ...prev,
        automation_paused: false,
        automation_pause_reason: null,
        status: 'active',
      }));
      showSuccess('Auto replies resumed for this conversation.');
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to resume auto replies.');
    } finally {
      setResumingAutomation(false);
    }
  };

  // Save the edited name using tempName
  const handleSaveName = async () => {
    const newName = (tempName || '').trim();
    if (!newName || !customerId) return;
    try {
      await saveCustomerName(customerId, newName);
      setCustomerName(newName);
      setIsEditingName(false);
      if (onNameUpdate) onNameUpdate(customerId, newName);
    } catch (error) {
      console.error("Failed to save customer name:", error);
    }
  };

  // inline header edit handlers
  const startInlineEdit = () => {
    setTempName(customerName || '');
    setIsEditingName(true);
  };

  const cancelInlineEdit = () => {
    setTempName(customerName || '');
    setIsEditingName(false);
  };

  const saveInlineEdit = async () => {
    if (!customerId) return;
    try {
      const newName = (tempName || '').trim();
      if (!newName) return;
      await saveCustomerName(customerId, newName);
      setCustomerName(newName);
      setIsEditingName(false);
      if (onNameUpdate) onNameUpdate(customerId, newName);
      // Refresh the customer list so the persisted name shows across the UI
      try { if (reloadCustomers) reloadCustomers(); } catch (e) { /* ignore */ }
    } catch (err) {
      console.error('Failed to save inline name', err);
    }
  };

  const displayNumber = React.useMemo(() => {
    if (!customerId) return '';
    return customerId.startsWith('+') ? customerId : `+${customerId}`;
  }, [customerId]);

  // Format phone number for display: +91 98787 61355
  const formattedNumber = React.useMemo(() => {
    const num = displayNumber.replace(/[^0-9+]/g, '');
    if (num.length > 10) {
      const cc = num.slice(0, num.length - 10);
      const rest = num.slice(num.length - 10);
      return `${cc} ${rest.slice(0, 5)} ${rest.slice(5)}`;
    }
    return num;
  }, [displayNumber]);

  if (!customerId) {
    return (
      <div className="placeholder-chat">
        <img src="/logo.png" alt="Blastmate Logo" style={{ width: 150, marginBottom: 20 }} />
        <h2>Blastmate for Web</h2>
        <p>Send and receive messages without keeping your phone online.<br />Use Blastmate on up to 4 linked devices and 1 phone at the same time.</p>
      </div>
    );
  }

  return (
    <div className="chat-window">
      <div className="chat-header">
        {onBack && (
          <button className="mobile-back-btn btn-icon small" aria-label="Back to chat list" onClick={onBack}>
            <ChevronLeft size={20} />
          </button>
        )}
        <div className="chat-header-avatar">
          <Avatar name={customerName || customerId} size={40} />
        </div>
        <div className="chat-info">
          {!isEditingName ? (
            <div className="contact-name-row">
              <span className="contact-name">{customerName || formattedNumber}</span>
              <button className="edit-name-btn" title="Edit name" onClick={startInlineEdit} aria-label="Edit contact name"><Edit2 size={14} /></button>
            </div>
          ) : (
            <div className="contact-edit-row">
              <input value={tempName} onChange={(e) => setTempName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveInlineEdit(); if (e.key === 'Escape') cancelInlineEdit(); }} placeholder="Enter name" autoFocus />
              <button className="edit-save-btn" title="Save" onClick={handleSaveName} aria-label="Save name"><Check size={14} /></button>
              <button className="edit-cancel-btn" title="Cancel" onClick={cancelInlineEdit} aria-label="Cancel edit">✕</button>
            </div>
          )}
          {customerName && (
            <div className="contact-subtitle">{formattedNumber}</div>
          )}
          {conversationState.automation_paused && (
            <div className="contact-subtitle" style={{ color: '#b45309' }}>
              Human takeover active. Auto replies are paused for this conversation.
            </div>
          )}
        </div>
        <div className="chat-actions">
          {conversationState.automation_paused && (
            <button
              className="btn"
              type="button"
              onClick={handleResumeAutomation}
              disabled={resumingAutomation}
              title="Resume auto replies for this conversation"
            >
              {resumingAutomation ? 'Resuming...' : 'Resume Auto'}
            </button>
          )}
          <button onClick={handleSearchClick} title="Search messages"><Search size={20} /></button>
          <button onClick={() => window.open(`https://wa.me/${displayNumber.replace(/[^0-9]/g, '')}`, '_blank')} title="Call via WhatsApp">
            <Phone size={20} />
          </button>
        </div>
      </div>

      {isSearchVisible && (
        <div className="chat-search-bar compact half" role="search">
          <div className="search-input-wrap">
            <Search size={16} className="icon-left" aria-hidden="true" />
            <input
              type="text"
              placeholder="Search messages"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) nextMatch();
                if (e.key === 'Enter' && e.shiftKey) prevMatch();
                if (e.key === 'Escape') { setSearchQuery(''); setIsSearchVisible(false); }
              }}
              aria-label="Search messages"
            />
            {searchQuery && (
              <button className="btn-icon clear" type="button" aria-label="Clear search" onClick={() => setSearchQuery('')}>
                <X size={14} />
              </button>
            )}
          </div>
          <div className="search-controls">
            {searchQuery && <span className="search-count" aria-live="polite">{messages.length}</span>}
            <div className="nav-buttons">
              <button className="btn-icon" type="button" onClick={prevMatch} title="Previous (Shift+Enter)" aria-label="Previous result"><ArrowUp size={14} /></button>
              <button className="btn-icon" type="button" onClick={nextMatch} title="Next (Enter)" aria-label="Next result"><ArrowDown size={14} /></button>
            </div>
          </div>
        </div>
      )}
      <div ref={messagesContainerRef} className="messages-area">
        {loading && messages.length === 0 && <p>Loading conversation...</p>}
        {messages.map((msg, idx) => {
          const isOutgoing = msg.direction === 'out';
          const msgDate = new Date(msg.ts);
          const prevDate = idx > 0 ? new Date(messages[idx - 1].ts) : null;
          const showDateSep = !prevDate ||
            msgDate.toDateString() !== prevDate.toDateString();

          // Format the date separator label
          const formatDateLabel = (d) => {
            const today = new Date();
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            if (d.toDateString() === today.toDateString()) return 'Today';
            if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
            return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
          };

          return (
            <React.Fragment key={msg.id || msg.ts}>
              {showDateSep && (
                <div className="date-separator">
                  <span>{formatDateLabel(msgDate)}</span>
                </div>
              )}
              <div
                className={`message ${isOutgoing ? 'sent' : 'received'}${msg.template_name ? ' template-msg' : ''}`}
                ref={el => { if (searchQuery) matchRefs.current[idx] = el; }}
              >
                {msg.template_name
                  ? <TemplateMsgCard msg={msg} searchQuery={searchQuery} highlightText={highlightText} templates={templates} />
                  : highlightText(msg.text || '', searchQuery)
                }
                <div className="message-meta">
                  <span className="timestamp">
                    {msgDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </span>
                  {isOutgoing && <TickIcon status={msg.status} />}
                </div>
              </div>
            </React.Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={handleSend} className="message-input-area">
        <button type="button"><Smile size={24} /></button>
        <button type="button"><Paperclip size={24} /></button>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message"
        />
        <button type="submit" disabled={!input.trim()}>
          {input.trim() ? <Send size={24} /> : <Mic size={24} />}
        </button>
      </form>
    </div>
  );
});

export default ChatWindow;
